import { Spinner } from '../components/feedback';
import { BrandMark } from '../components/BrandMark';
import { useEffect, useState } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router';
import { api3 } from '../api/client-p3';
import type { TokenScope } from '../api/types';
import type { OAuthConsent as Consent } from '../api/types-p3';
import { errorMessage, useApp } from '../app-context';

/** MCP クライアント（Claude など）に、このパネルの操作を許可する画面 */
export function OAuthConsent() {
  const { me } = useApp();
  const location = useLocation();
  const [params] = useSearchParams();
  const requestId = params.get('request') ?? '';
  const [consent, setConsent] = useState<Consent>();
  const [scopes, setScopes] = useState<TokenScope[]>(['read']);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!me?.user || !requestId) return;
    api3
      .oauthConsent(requestId)
      .then((result) => {
        setConsent(result);
        setScopes(result.scopes);
      })
      .catch((caught: unknown) => setError(errorMessage(caught)));
  }, [me?.user, requestId]);

  if (!me) return null;
  if (!me.user)
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;

  const decide = async (approve: boolean) => {
    setBusy(true);
    try {
      const { redirectTo } = await api3.decideOAuthConsent(requestId, approve, scopes);
      window.location.assign(redirectTo);
    } catch (caught) {
      setError(errorMessage(caught));
      setBusy(false);
    }
  };

  const toggle = (scope: TokenScope) =>
    setScopes(scopes.includes(scope) ? scopes.filter((s) => s !== scope) : [...scopes, scope]);

  return (
    <div className="auth-page">
      <div className="card auth-card" style={{ width: 'min(440px, 100%)' }}>
        <div className="brand" style={{ padding: 0 }}>
          <BrandMark />
          mc-panel
        </div>
        {error && <div className="alert danger">{error}</div>}
        {!consent && !error && <p className="muted">読み込み中…</p>}
        {consent && (
          <>
            <div className="stack" style={{ gap: 4 }}>
              <h1>AI の接続を許可しますか？</h1>
              <p className="muted" style={{ margin: 0 }}>
                <strong>{consent.clientName}</strong> が、<strong>{me.user.name}</strong>{' '}
                としてこのパネルを操作しようとしています。
              </p>
              {consent.clientUri && (
                <p className="small faint" style={{ margin: 0 }}>
                  {consent.clientUri}
                </p>
              )}
            </div>
            <div className="stack" style={{ gap: 8 }}>
              <label className="check">
                <input type="checkbox" checked={scopes.includes('read')} onChange={() => toggle('read')} />
                サーバーの状態・ログ・監視値の閲覧
              </label>
              <label className="check">
                <input
                  type="checkbox"
                  checked={scopes.includes('operate')}
                  disabled={!consent.scopes.includes('operate')}
                  onChange={() => toggle('operate')}
                />
                起動・停止・許可されたコンソールコマンド
              </label>
              <p className="small faint" style={{ margin: 0 }}>
                削除・ソフト変更などは許可しても AI だけでは実行できず、承認待ちになります。
              </p>
            </div>
            <p className="small faint" style={{ margin: 0 }}>
              許可後の戻り先: <code>{consent.redirectUri}</code>
            </p>
            <div className="row">
              <span className="spacer" />
              <button className="btn" disabled={busy} onClick={() => void decide(false)}>
                {busy && <Spinner />}
                拒否
              </button>
              <button className="btn primary" disabled={busy || scopes.length === 0} onClick={() => void decide(true)}>
                {busy && <Spinner />}
                許可する
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
